import { computed, ref } from 'vue'
import type {
  UseSearchFilters,
  UseSearchFiltersProps,
  CategoryFilterFn,
  SearchFilterFn
} from 'components/responsive-search-filters/types'
import { DEFAULT_CATEGORY_FILTER } from 'components/responsive-search-filters/types'

const defaultSearchFilter: SearchFilterFn<any> = (items, query) => {
  if (!query.trim()) return items

  const lowerQuery = query.toLowerCase()
  return items.filter(item => {
    const name = (item.displayName || item.name || item.title || '').toLowerCase()
    return name.includes(lowerQuery)
  })
}

const defaultCategoryFilter: CategoryFilterFn<any> = (items, _category) => {
  return items
}

export const useSearchFilters = <T = any>(
  props: UseSearchFiltersProps<T>
): UseSearchFilters<T> => {
  const selectedCategory = ref<string>(props.initialCategory || DEFAULT_CATEGORY_FILTER)
  const queryString = ref<string>('')

  const builtInCategoryFilter: CategoryFilterFn<T> = (items, category) => {
    if (!props.categoryKeywords) return items

    const keywords = props.categoryKeywords[category]
    if (!keywords) return items

    const field = props.categoryTypeField || 'type'

    return items.filter(item => {
      const typeValue = String((item as any)[field] || '').toLowerCase()
      return keywords.some(keyword => typeValue.includes(keyword.toLowerCase()))
    })
  }

  const categoryFilterFn = props.categoryFilterFn ||
                           (props.categoryKeywords ? builtInCategoryFilter : defaultCategoryFilter)
  const searchFilterFn = props.searchFilterFn || defaultSearchFilter

  const setQueryString = (query: string) => {
    queryString.value = query
  }

  const selectCategory = (category: string) => {
    selectedCategory.value = category
  }

  const reset = () => {
    selectedCategory.value = props.initialCategory || DEFAULT_CATEGORY_FILTER
    queryString.value = ''
  }

  const filteredItems = computed(() => {
    let items = Array.isArray(props.items)
      ? props.items
      : (props.items as any).value || []

    if (selectedCategory.value !== DEFAULT_CATEGORY_FILTER) {
      items = categoryFilterFn(items, selectedCategory.value)
    }

    items = searchFilterFn(items, queryString.value)

    return items
  })

  const showEmptyState = computed(() =>
    filteredItems.value.length === 0 &&
    queryString.value.trim().length > 0
  )

  return {
    selectedCategory: computed(() => selectedCategory.value),
    queryString: computed(() => queryString.value),
    filteredItems,
    showEmptyState,
    selectCategory,
    setQueryString,
    reset
  }
}
